import {
    faAdd,
    faBriefcase,
    faClose,
    faKey,
    faPencil,
    faUser,
    faSave,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useRef, useState } from 'react';
import { notification } from 'antd';
import {
    alert,
    AlertType,
    Button,
    ConfirmModal,
    Input,
} from '@quyva/shared-ui';
import { getUrl } from '../../pdfDisplay/utils';
import axios from 'axios';
import { Config } from 'config/envConfig';
import {
    isEmailValid,
    isValidContactNumber,
} from '@quyva/common';
import { useSettingsContext } from '../../../context';

const handleDelete = async (user, accountDetails, setAccountDetails) => {
    try {
        const dynamicUrl = getUrl(
            `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/customer_chat_routes/remove-vendor`
        );
        const encodedToken = encodeURIComponent(user.token);

        // Create FormData to send the token
        const formData = new FormData();
        formData.append('token', user.token);

        // Send the DELETE request with the FormData as the second argument
        await axios.delete(dynamicUrl, {
            data: formData,
            headers: {
                Authorization: encodedToken,
            },
        });

        const accountData = { ...accountDetails };
        delete accountData['vendorDetails'];
        setAccountDetails(accountData);
    } catch (error) {
        console.error('Error removing vendor:', error); // Add error logging for debugging
    }
};

export const AccountSettingsComponent = ({
    user,
    accountDetails,
    setAccountDetails,
    SettingSections,
    isSmallScreen,
}) => {
    const {
        loadAccountSection,
        runWithSaving,
        saveAccountSection,
        isAccountDirty,
        passwordDirty,
        newPassword,
        setNewPassword,
        confirmPassword,
        setConfirmPassword,
    } = useSettingsContext();
    const { personalDetails } = accountDetails;
    const [viewDeleteConfirmModal, setViewDeleteConfirmModal] = useState(false);
    const [showPasswordSection, setShowPasswordSection] = useState(false);
    const [newPasswordVisible, setNewPasswordVisible] = useState(false);
    const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);

    useEffect(() => {
        loadAccountSection?.();
    }, []);

    useEffect(() => {
        if (!showPasswordSection) {
            setNewPassword('');
            setConfirmPassword('');
        }
    }, [showPasswordSection, setNewPassword, setConfirmPassword]);

    return (
        <div className='mb-6'>
            {viewDeleteConfirmModal && (
                <ConfirmModal
                    modalText={
                        'Delete this product support account and remove all file and product mappings related to this account?'
                    }
                    onCancel={() => setViewDeleteConfirmModal(false)}
                    onConfirm={() => {
                        handleDelete(user, accountDetails, setAccountDetails);
                        setViewDeleteConfirmModal(false);
                    }}
                />
            )}

            <div
                className={`flex items-center justify-between font-semibold mb-6 py-3 px-4 text-texts-secondary300 uppercase bg-backgrounds-primary100 truncate`}
            >
                <div className='flex items-center'>
                    <FontAwesomeIcon icon={faUser} className='mr-2' />
                    <div className='font-semibold text-texts-secondary300 uppercase truncate'>
                        {SettingSections.ACCOUNT}
                    </div>
                </div>
                <Button
                    type='success'
                    icon={faSave}
                    text={'Save'}
                    disabled={!isAccountDirty && !passwordDirty}
                    hoverText='Save account settings'
                    onClick={() =>
                        runWithSaving(async () => {
                            await saveAccountSection();
                            setShowPasswordSection(false);
                        })
                    }
                />
            </div>

            <div className='h-[calc(100vh-210px)] w-full overflow-auto px-6 relative'>
                <div className='flex items-center mb-6'>
                    <div className='w-1/4 font-semibold text-texts-secondary300'>
                        <label htmlFor='userName'>Name</label>
                    </div>

                    <div className='w-full md:w-1/4'>
                        <div className='relative'>
                            <Input
                                type={'text'}
                                value={
                                    personalDetails && personalDetails.username
                                        ? personalDetails.username
                                        : ''
                                }
                                disabled={true}
                            />
                        </div>
                    </div>
                </div>

                <div className='flex items-center mb-6'>
                    <div className='w-1/4 font-semibold text-texts-secondary300'>
                        <label htmlFor='email'>Email</label>
                    </div>

                    <div className='w-full md:w-1/4'>
                        <div className='relative'>
                            <Input
                                type={'text'}
                                value={
                                    personalDetails && personalDetails.email
                                        ? personalDetails.email
                                        : ''
                                }
                                disabled={true}
                            />
                        </div>
                    </div>
                </div>

                <div className='flex items-center mb-8'>
                    <div
                        className={`w-1/4 font-semibold text-texts-secondary300 ${isSmallScreen ? 'pr-2' : ''
                            }`}
                    >
                        <label htmlFor='currency'>Currency</label>
                    </div>

                    <div className='w-full md:w-1/4'>
                        <div className='relative'>
                            <Input
                                type={'text'}
                                value={
                                    personalDetails && personalDetails.currency
                                        ? personalDetails.currency
                                        : ''
                                }
                                disabled={true}
                            />
                        </div>
                    </div>
                </div>

                <div className='flex items-center mb-8'>
                    <div
                        className={`w-1/4 font-semibold text-texts-secondary300 ${isSmallScreen ? 'pr-2' : ''
                            }`}
                    >
                        <label htmlFor='password'>Password</label>
                    </div>
                    <Button
                        text={showPasswordSection ? 'Cancel Password Change' : 'Change Password'}
                        icon={faKey}
                        type={'success'}
                        hoverText={'Change password.'}
                        onClick={() => setShowPasswordSection(!showPasswordSection)}
                    />
                </div>
                {showPasswordSection && (
                    <div className="w-[90vw] min-w-[90px] md:w-[60vw] md:min-w-[500px] px-6 py-6 rounded-xl bg-backgrounds-primary100 border border-backgrounds-primary200">

                        <div className="flex flex-col w-full text-texts-secondary300">

                            <div className="flex items-center mb-4">
                                <label className="w-1/4 font-semibold text-texts-secondary300">
                                    New Password
                                </label>

                                <Input
                                    maxLength={20}
                                    hoverText="Type in your New Password"
                                    type={newPasswordVisible ? 'text' : 'password'}
                                    className="flex-1 p-3 border rounded-xl bg-backgrounds-slate hover:border-backgrounds-primary300 focus:outline-backgrounds-primary200"
                                    placeholder="New Password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                />

                                <button
                                    onClick={() => setNewPasswordVisible(!newPasswordVisible)}
                                    className="ml-2 text-backgrounds-primary400"
                                >
                                    {newPasswordVisible ? 'Hide' : 'Show'}
                                </button>
                            </div>

                            <div className="flex items-center">
                                <label className="w-1/4 font-semibold text-texts-secondary300">
                                    Confirm Password
                                </label>

                                <Input
                                    maxLength={20}
                                    hoverText="Verify your New Password"
                                    type={confirmPasswordVisible ? 'text' : 'password'}
                                    className="flex-1 p-3 border rounded-xl bg-backgrounds-slate hover:border-backgrounds-primary300 focus:outline-backgrounds-primary200"
                                    placeholder="Confirm New Password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                />

                                <button
                                    onClick={() => setConfirmPasswordVisible(!confirmPasswordVisible)}
                                    className="ml-2 text-backgrounds-primary400"
                                >
                                    {confirmPasswordVisible ? 'Hide' : 'Show'}
                                </button>
                            </div>

                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
